import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { forwardRef, useId, type ComponentProps, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/* shadcn/ui-style components (S1-T10), sized for outdoor phone use (G-13: ≥ 48 px targets, 4.5:1 contrast). */

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-brand-800 text-white hover:bg-brand-900 active:bg-brand-900',
        secondary: 'bg-white text-stone-900 border border-stone-300 hover:bg-stone-100',
        ghost: 'text-stone-800 hover:bg-stone-200',
        danger: 'bg-red-700 text-white hover:bg-red-800',
        warning: 'bg-amber-500 text-stone-950 hover:bg-amber-600',
      },
      size: {
        md: 'h-12 px-4 text-base',
        lg: 'h-14 px-6 text-lg',
        sm: 'h-10 px-3 text-sm',
        icon: 'h-12 w-12',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ComponentProps<'button'> & VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, type = 'button', ...props }, ref) => (
  <button ref={ref} type={type} className={cn(buttonVariants({ variant, size }), className)} {...props} />
));
Button.displayName = 'Button';

const fieldBase =
  'w-full rounded-xl border border-stone-300 bg-white px-3 text-base text-stone-900 placeholder:text-stone-400 focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-700/30 disabled:bg-stone-100 aria-[invalid=true]:border-red-600';

export const Input = forwardRef<HTMLInputElement, ComponentProps<'input'>>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn(fieldBase, 'h-12', className)} {...props} />
));
Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, ComponentProps<'textarea'>>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(fieldBase, 'min-h-24 py-2', className)} {...props} />
));
Textarea.displayName = 'Textarea';

/** Native select: the best picker on Android. */
export const Select = forwardRef<HTMLSelectElement, ComponentProps<'select'>>(({ className, ...props }, ref) => (
  <select ref={ref} className={cn(fieldBase, 'h-12 appearance-auto', className)} {...props} />
));
Select.displayName = 'Select';

export function Label({ className, ...props }: ComponentProps<'label'>) {
  return <label className={cn('mb-1 block text-sm font-medium text-stone-700', className)} {...props} />;
}

/** Label + control + hint/error. The child receives the generated id when it is a single element using `id`. */
export function Field({
  label,
  error,
  hint,
  children,
  htmlFor,
  className,
}: {
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1', className)}>
      {label ? <Label htmlFor={htmlFor}>{label}</Label> : null}
      {children}
      {error ? <p className="text-sm text-red-700">{error}</p> : hint ? <p className="text-sm text-stone-500">{hint}</p> : null}
    </div>
  );
}

export function useFieldId(): string {
  return useId();
}

export function Card({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('rounded-2xl border border-stone-200 bg-white p-4 shadow-sm', className)} {...props} />;
}

export function CardTitle({ className, ...props }: ComponentProps<'h2'>) {
  return <h2 className={cn('mb-3 text-lg font-semibold text-stone-900', className)} {...props} />;
}

const badgeVariants = cva('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-medium whitespace-nowrap', {
  variants: {
    tone: {
      neutral: 'bg-stone-200 text-stone-800',
      green: 'bg-green-100 text-green-900',
      blue: 'bg-sky-100 text-sky-900',
      amber: 'bg-amber-100 text-amber-900',
      red: 'bg-red-100 text-red-900',
      purple: 'bg-violet-100 text-violet-900',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export function Badge({ className, tone, ...props }: ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full border-collapse text-left text-sm', className)} {...props} />
    </div>
  );
}
export const Th = ({ className, ...props }: ComponentProps<'th'>) => (
  <th className={cn('border-b border-stone-200 px-2 py-2 font-semibold text-stone-700', className)} {...props} />
);
export const Td = ({ className, ...props }: ComponentProps<'td'>) => (
  <td className={cn('border-b border-stone-100 px-2 py-2 align-top', className)} {...props} />
);

export const Tabs = TabsPrimitive.Root;
export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn('flex gap-1 overflow-x-auto rounded-xl bg-stone-200 p-1', className)} {...props} />;
}
export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'min-h-11 flex-1 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-stone-700 data-[state=active]:bg-white data-[state=active]:text-stone-950 data-[state=active]:shadow',
        className,
      )}
      {...props}
    />
  );
}
export const TabsContent = ({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content className={cn('mt-4', className)} {...props} />
);

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <DialogPrimitive.Content className="fixed inset-x-3 top-1/2 z-50 mx-auto max-h-[90vh] max-w-lg -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-xl focus:outline-none">
          <div className="mb-3 flex items-start justify-between gap-3">
            <DialogPrimitive.Title className="text-lg font-semibold">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close className="-m-2 rounded-lg p-2 text-stone-500 hover:bg-stone-100" aria-label="Close">
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
          {description ? <DialogPrimitive.Description className="mb-3 text-stone-700">{description}</DialogPrimitive.Description> : null}
          {children}
          {footer ? <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Bottom sheet (mobile menus). */
export function Sheet({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (o: boolean) => void; title: ReactNode; children: ReactNode }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <DialogPrimitive.Content className="safe-bottom fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-4 shadow-2xl focus:outline-none">
          <DialogPrimitive.Title className="mb-3 text-center text-lg font-semibold">{title}</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Segmented control (sessions, tabs of forms). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode }>;
  className?: string;
}) {
  return (
    <div role="radiogroup" className={cn('flex rounded-xl bg-stone-200 p-1', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'min-h-11 flex-1 rounded-lg px-2 text-sm font-medium',
            value === o.value ? 'bg-white text-stone-950 shadow' : 'text-stone-700',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Checkbox({ label, className, ...props }: ComponentProps<'input'> & { label: ReactNode }) {
  return (
    <label className={cn('flex min-h-12 cursor-pointer items-center gap-3 rounded-xl px-1', className)}>
      <input type="checkbox" className="h-6 w-6 accent-brand-800" {...props} />
      <span>{label}</span>
    </label>
  );
}

export function PageHeader({ title, actions, back }: { title: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      {back}
      <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-stone-950">{title}</h1>
      {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="rounded-2xl border border-dashed border-stone-300 p-6 text-center text-stone-600">{children}</div>;
}

export function Spinner({ className }: { className?: string }) {
  return <span className={cn('inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent', className)} />;
}

export function Stat({ label, value, sub, tone }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: 'up' | 'down' | 'none' }) {
  return (
    <Card className="p-3">
      <div className="text-sm text-stone-600">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-stone-950">{value}</div>
      {sub ? (
        <div className={cn('mt-0.5 text-sm', tone === 'up' ? 'text-green-800' : tone === 'down' ? 'text-red-700' : 'text-stone-600')}>{sub}</div>
      ) : null}
    </Card>
  );
}
